import {useCallback, useMemo, useState} from 'react'
import {ActionList, ActionMenu, Button, Checkbox, Dialog, Flash, IconButton, Spinner} from '@primer/react'
import {AgentIcon, GitCommitIcon, GitPullRequestIcon, TriangleDownIcon} from '@primer/octicons-react'
import {useClickAnalytics} from '@github-ui/use-analytics'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {UnsafeHTMLDiv} from '@github-ui/safe-html/UnsafeHTML'
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-imports
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {ModelPicker} from './ModelPicker'
import styles from './FixWithCopilotDialog.module.css'
import type {AutomatedSuggestion, ThreadComment} from './types'
import {buildThreadCommentsFromSuggestion, resolveThreadCommentSuggestion, toStaticDiffLine} from './suggestion-helpers'

export interface FixWithCopilotDialogProps {
  title: string
  commentPermalink: string
  isOverviewComment: boolean
  commentAnchor: string
  basePath?: string
  modelsUrl?: string
  threadComments?: ThreadComment[]
  /**
   * Raw suggestion data from an automated review comment. When provided alongside
   * `commentMessage` and `suggestionState`, the dialog will build thread comments
   * internally from the structured diff data.
   */
  suggestion?: AutomatedSuggestion
  suggestionState?: string
  commentMessage?: string
}

type ApplyChangesMode = 'commit' | 'pull_request'

export const FixWithCopilotDialog: React.FC<FixWithCopilotDialogProps> = ({
  title,
  commentPermalink,
  isOverviewComment,
  commentAnchor,
  basePath,
  modelsUrl,
  threadComments: threadCommentsProp,
  suggestion,
  suggestionState,
  commentMessage,
}) => {
  const {sendClickAnalyticsEvent} = useClickAnalytics()

  // Build thread comments from raw suggestion data when provided; otherwise
  // fall back to the pre-built threadComments prop. Resolve any structured
  // suggestion data into rendered HTML on the client side.
  const threadComments = useMemo(() => {
    if (threadCommentsProp) return threadCommentsProp.map(resolveThreadCommentSuggestion)
    if (!commentMessage) return undefined
    return buildThreadCommentsFromSuggestion(commentMessage, commentPermalink, suggestion, suggestionState)
  }, [threadCommentsProp, commentMessage, commentPermalink, suggestion, suggestionState])

  const defaultMode: ApplyChangesMode = 'commit'

  const [applyMode, setApplyMode] = useState<ApplyChangesMode>(defaultMode)
  const [isFixing, setIsFixing] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [currentModalSessionId, setCurrentModalSessionId] = useState('')
  const [additionalInstructions, setAdditionalInstructions] = useState('')
  const [selectedModel, setSelectedModel] = useState('auto')
  const [taskUrl, setTaskUrl] = useState<string | null>(null)
  const [selectedComments, setSelectedComments] = useState<Set<number>>(
    () => new Set(threadComments?.map((_, i) => i) ?? []),
  )

  const noCommentsSelected = isOverviewComment && selectedComments.size === 0

  const generateModalSessionId = (): string => {
    return crypto.randomUUID()
  }

  const applyModeLabel = applyMode === 'commit' ? 'Fix and commit' : 'Fix and open pull request'
  const ApplyModeIcon = applyMode === 'commit' ? GitCommitIcon : GitPullRequestIcon

  const handleModalClick = useCallback(() => {
    const modalSessionId = generateModalSessionId()
    setCurrentModalSessionId(modalSessionId)

    sendClickAnalyticsEvent({
      category: 'ccr_fix_with_copilot',
      action: 'dialog_open',
      commentAnchor,
      modalSessionId,
      applyMode,
      fixAll: isOverviewComment,
      model: selectedModel,
    })

    setIsDialogOpen(true)
  }, [sendClickAnalyticsEvent, commentAnchor, applyMode, isOverviewComment, selectedModel])

  const onClose = useCallback(() => {
    setIsDialogOpen(false)
    setErrorMessage('')
    setAdditionalInstructions('')
    setSelectedComments(new Set(threadComments?.map((_, i) => i) ?? []))
  }, [threadComments])

  const handleSubmit = useCallback(async () => {
    setIsFixing(true)
    setErrorMessage('')

    const trimmedInstructions = additionalInstructions.trim()

    const allSelected = !threadComments || (isOverviewComment && selectedComments.size === threadComments.length)
    const reviewReferences =
      isOverviewComment && threadComments
        ? threadComments.filter((_, i) => selectedComments.has(i)).map(tc => tc.permalink)
        : [commentPermalink]

    try {
      const response = await verifiedFetchJSON(`${basePath}/code_review_fix_with_copilot`, {
        method: 'POST',
        body: {
          create_pull_request: applyMode === 'pull_request',
          fix_all: allSelected,
          review_references: reviewReferences,
          ...(trimmedInstructions ? {additional_instructions: trimmedInstructions} : {}),
          ...(selectedModel && selectedModel !== 'auto' ? {model: selectedModel} : {}),
        },
      })

      if (!response.ok) {
        let data = null
        try {
          data = await response.json()
        } catch {
          // ignore JSON parse errors
        }
        throw new Error(data?.error || data?.message || `Failed to Fix with Copilot (${response.status})`)
      }

      const data = await response.json()

      sendClickAnalyticsEvent({
        category: 'ccr_fix_with_copilot',
        action: 'submit',
        commentAnchor,
        modalSessionId: currentModalSessionId,
        applyMode,
        fixAll: allSelected,
        model: selectedModel,
      })

      if (data?.task_url) {
        setTaskUrl(data.task_url)
      }

      onClose()
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.')
    } finally {
      setIsFixing(false)
    }
  }, [
    basePath,
    additionalInstructions,
    isOverviewComment,
    commentPermalink,
    sendClickAnalyticsEvent,
    commentAnchor,
    currentModalSessionId,
    applyMode,
    onClose,
    selectedModel,
    selectedComments,
    threadComments,
  ])

  if (taskUrl) {
    return (
      <Button size="small" variant="default" as="a" href={taskUrl} leadingVisual={AgentIcon}>
        View Copilot task
      </Button>
    )
  }

  return (
    <>
      <Button
        size="small"
        variant="default"
        leadingVisual={AgentIcon}
        onClick={handleModalClick}
        data-testid={`copilot-apply-changes-button-${commentAnchor}`}
      >
        {title}
      </Button>
      {isDialogOpen && (
        <Dialog onClose={onClose} title={title} width="xlarge">
          <Dialog.Body>
            {errorMessage && (
              <Flash variant="danger" className={styles.errorBanner}>
                {errorMessage}
              </Flash>
            )}
            <div className={styles.textareaContainer}>
              <textarea
                className={styles.instructionsTextarea}
                placeholder="Add additional instructions..."
                value={additionalInstructions}
                onChange={e => setAdditionalInstructions(e.target.value)}
                rows={6}
              />
              <div className={styles.textareaToolbar}>
                {modelsUrl && <ModelPicker modelsUrl={modelsUrl} onModelChange={setSelectedModel} />}
                <div className={styles.splitButton}>
                  <Button
                    size="small"
                    variant="invisible"
                    className={`${styles.contributionMethodButton}${noCommentsSelected ? ` ${styles.clearVariant}` : ''}`}
                    leadingVisual={ApplyModeIcon}
                    onClick={handleSubmit}
                    disabled={isFixing || noCommentsSelected}
                  >
                    {isFixing ? (
                      <>
                        <Spinner size="small" /> Fixing…
                      </>
                    ) : (
                      applyModeLabel
                    )}
                  </Button>
                  <ActionMenu>
                    <ActionMenu.Anchor>
                      <IconButton
                        size="small"
                        variant="invisible"
                        className={`${styles.contributionMethodDropdown}${noCommentsSelected ? ` ${styles.clearVariant}` : ''}`}
                        icon={TriangleDownIcon}
                        aria-label="Change contribution method"
                        disabled={isFixing}
                      />
                    </ActionMenu.Anchor>
                    <ActionMenu.Overlay width="medium">
                      <ActionList selectionVariant="single">
                        <ActionList.Item selected={applyMode === 'commit'} onSelect={() => setApplyMode('commit')}>
                          <ActionList.LeadingVisual>
                            <GitCommitIcon size={16} />
                          </ActionList.LeadingVisual>
                          Commit to this pull request
                          <ActionList.Description variant="block">
                            Copilot will push a commit directly to this PR&apos;s branch.
                          </ActionList.Description>
                        </ActionList.Item>
                        <ActionList.Item
                          selected={applyMode === 'pull_request'}
                          onSelect={() => setApplyMode('pull_request')}
                        >
                          <ActionList.LeadingVisual>
                            <GitPullRequestIcon size={16} />
                          </ActionList.LeadingVisual>
                          Open a new pull request
                          <ActionList.Description variant="block">
                            Copilot will create a new pull request with the changes.
                          </ActionList.Description>
                        </ActionList.Item>
                      </ActionList>
                    </ActionMenu.Overlay>
                  </ActionMenu>
                </div>
              </div>
            </div>
            {isOverviewComment && threadComments && threadComments.length > 0 && (
              <div className={styles.commentsSection}>
                <h4 className={`${styles.commentsHeading}${noCommentsSelected ? ` ${styles.danger}` : ''}`}>
                  {`${selectedComments.size > 0 ? 'Comments to fix' : 'Add comments to fix'}: (${selectedComments.size}/${threadComments.length})`}
                </h4>
                {threadComments.map((tc, index) => (
                  <div key={tc.permalink} className={styles.commentsList}>
                    <div className={styles.commentItem}>
                      <label className={styles.commentLabel}>
                        <Checkbox
                          checked={selectedComments.has(index)}
                          onChange={() => {
                            setSelectedComments(prev => {
                              const next = new Set(prev)
                              if (next.has(index)) {
                                next.delete(index)
                              } else {
                                next.add(index)
                              }
                              return next
                            })
                          }}
                          className={styles.commentCheckbox}
                        />
                        <span className={styles.commentFilename}>{tc.filename}</span>
                      </label>
                      <hr className={styles.commentDivider} />
                      <SuggestionContent comment={tc} />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Dialog.Body>
        </Dialog>
      )}
    </>
  )
}

/**
 * Renders the suggestion content for a thread comment. Uses the structured
 * `AutomatedSuggestion` data with `StaticUnifiedDiffPreview` when available,
 * falling back to pre-rendered `suggestionHtml` or plain message text.
 */
function SuggestionContent({comment}: {comment: ThreadComment}) {
  const {suggestion, suggestionState, descriptionHtml, message, hasSuggestion, suggestionHtml} = comment

  if (suggestion && suggestionState === 'present') {
    return (
      <div className={styles.commentMessage}>
        <UnsafeHTMLDiv className="mb-2" html={descriptionHtml || message} />
        <div className="text-small color-fg-muted mb-1">
          <strong>Suggested changeset</strong>
        </div>
        {suggestion.diffEntries.map(entry => (
          <StaticUnifiedDiffPreview
            key={entry.path}
            diffLines={entry.diffLines.map(toStaticDiffLine)}
            tabSize={4}
            className={styles.suggestionDiff}
          />
        ))}
      </div>
    )
  }

  if (hasSuggestion && suggestionHtml) {
    return <UnsafeHTMLDiv className={styles.commentMessage} html={suggestionHtml} />
  }

  return <p className={styles.commentMessage}>{message}</p>
}
